import { Users, X } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  AdminBulkActionRow,
  AdminSectionCard,
  AdminStatCard,
  ConfirmDialog,
  EmptyState,
  Modal,
  Pill,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { ADMIN_ACTION_BUTTON, FIELD_INPUT } from '@/shared/ui-class';
import {
  SEED_ADMIN_USERS,
  type AdminUserSummary,
  type UserStatus,
} from '@/versions/v1/shared/admin-seed';
import { AdminRowMenu } from './AdminRowMenu';
import { UserDetailDrawer } from './UserDetailDrawer';
import {
  AdminSceneShell,
  CURRENT_USER_ID,
  formatAdminDate,
} from './admin-shared';
import {
  countUsersByStatus,
  filterUsers,
  statusLabel,
  statusPillTone,
  type UserStatusFilter,
} from './UsersScene.transform';

const STATUS_FILTER_LABELS: Record<UserStatus, string> = {
  active: 'Active',
  pending: 'Awaiting',
  blocked: 'Blocked',
  rejected: 'Rejected',
};

export function UsersScene() {
  const [users, setUsers] = useState(SEED_ADMIN_USERS);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<UserStatusFilter>(null);
  const [viewUser, setViewUser] = useState<AdminUserSummary | null>(null);
  const [drawerDeleteOpen, setDrawerDeleteOpen] = useState(false);
  const [blockTarget, setBlockTarget] = useState<AdminUserSummary | null>(null);
  const [blockReason, setBlockReason] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<AdminUserSummary | null>(null);

  const counts = useMemo(() => countUsersByStatus(users), [users]);
  const filteredUsers = useMemo(
    () => filterUsers(users, query, statusFilter),
    [users, query, statusFilter],
  );

  const mutateUser = (userId: string, patch: Partial<AdminUserSummary>) => {
    setUsers((prev) =>
      prev.map((user) => (user.id === userId ? { ...user, ...patch } : user)),
    );
  };

  const blockUser = (user: AdminUserSummary) => {
    mutateUser(user.id, { status: 'blocked' });
  };

  const unblockUser = (user: AdminUserSummary) => {
    mutateUser(user.id, { status: 'active' });
  };

  const returnToLobby = (user: AdminUserSummary) => {
    mutateUser(user.id, { status: 'pending' });
  };

  const makeAdmin = (user: AdminUserSummary) => {
    mutateUser(user.id, { platformRole: 'admin', status: 'active' });
  };

  const removeAdmin = (user: AdminUserSummary) => {
    mutateUser(user.id, { platformRole: null });
  };

  const deleteUser = (userId: string) => {
    setUsers((prev) => prev.filter((user) => user.id !== userId));
  };

  const renderUserPills = (user: AdminUserSummary) => (
    <div className="flex flex-wrap gap-1">
      <Pill tone={statusPillTone(user.status)}>{statusLabel(user.status)}</Pill>
      {user.platformRole === 'admin' ? <Pill tone="admin">admin</Pill> : null}
      {user.isEnvAdmin ? (
        <Pill title="Bootstrap admin from ADMIN_USER_IDS — cannot be modified here">ENV</Pill>
      ) : null}
      {user.id === CURRENT_USER_ID ? <Pill>YOU</Pill> : null}
    </div>
  );

  const filterChip =
    statusFilter != null ? (
      <span className="inline-flex items-center gap-1.5">
        <Pill tone={statusPillTone(statusFilter)}>
          {STATUS_FILTER_LABELS[statusFilter]}
        </Pill>
        <button
          type="button"
          onClick={() => setStatusFilter(null)}
          className={cn(ADMIN_ACTION_BUTTON, 'inline-flex items-center px-1.5')}
          aria-label="Clear status filter"
        >
          <X className="h-3 w-3" aria-hidden />
        </button>
      </span>
    ) : (
      <span>{counts.total} users</span>
    );

  return (
    <AdminSceneShell
      sceneName="UsersScene"
      title="Users"
      description="Search, filter, and manage every account — status, roles, and access."
      stackClassName="space-y-4"
    >
      <Commentable name="StatCards">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {(
            [
              {
                key: 'total',
                label: 'Total',
                value: counts.total,
                filter: null as UserStatusFilter,
                tone: 'default' as const,
              },
              {
                key: 'active',
                label: 'Active',
                value: counts.active,
                filter: 'active' as const,
                tone: 'default' as const,
              },
              {
                key: 'pending',
                label: 'Awaiting',
                value: counts.pending,
                filter: 'pending' as const,
                tone: 'warning' as const,
              },
              {
                key: 'blocked',
                label: 'Blocked',
                value: counts.blocked,
                filter: 'blocked' as const,
                tone: 'danger' as const,
              },
            ] as const
          ).map((tile) => {
            const selected =
              tile.filter === null
                ? statusFilter === null
                : statusFilter === tile.filter;
            return (
              <AdminStatCard
                key={tile.key}
                label={tile.label}
                value={tile.value}
                tone={tile.tone}
                onClick={() => setStatusFilter(tile.filter)}
                active={selected}
              />
            );
          })}
        </div>
      </Commentable>

      <Commentable name="Filters">
        <div>
          <AdminBulkActionRow left={filterChip}>
            <label htmlFor="user-admin-search" className="sr-only">
              Search
            </label>
            <input
              id="user-admin-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by email or name"
              className={cn('w-full min-w-[12rem] sm:w-64', FIELD_INPUT)}
            />
          </AdminBulkActionRow>
        </div>
      </Commentable>

      <Commentable name="Table">
        <div>
          <AdminSectionCard>
            {filteredUsers.length === 0 ? (
              <EmptyState
                embedded
                icon={<Users className="h-8 w-8 text-zinc-400" />}
                title="No users match"
                description={
                  statusFilter || query.trim()
                    ? 'Try clearing filters or a different search term.'
                    : 'Try a different search term.'
                }
              />
            ) : (
              <Table wrap={false}>
                <THead>
                  <TR>
                    <TH>Email</TH>
                    <TH>Name</TH>
                    <TH>Status</TH>
                    <TH>Role</TH>
                    <TH>Created</TH>
                    <TH className="text-right" aria-label="Actions" />
                  </TR>
                </THead>
                <TBody>
                  {filteredUsers.map((user) => (
                    <TR key={user.id}>
                      <TD>{user.email}</TD>
                      <TD>{user.name ?? '—'}</TD>
                      <TD>{renderUserPills(user)}</TD>
                      <TD>
                        {user.platformRole === 'admin' ? (
                          <Pill tone="admin">admin</Pill>
                        ) : (
                          '—'
                        )}
                      </TD>
                      <TD>{formatAdminDate(user.createdAt)}</TD>
                      <TD className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {user.status === 'blocked' ? (
                            <button
                              type="button"
                              disabled={user.isEnvAdmin}
                              onClick={() => unblockUser(user)}
                              className={ADMIN_ACTION_BUTTON}
                            >
                              Unblock
                            </button>
                          ) : (
                            <button
                              type="button"
                              disabled={
                                user.isEnvAdmin ||
                                user.status === 'pending' ||
                                user.status === 'rejected'
                              }
                              onClick={() => setBlockTarget(user)}
                              className={cn(
                                ADMIN_ACTION_BUTTON,
                                'text-red-600 dark:text-red-400',
                              )}
                            >
                              Block
                            </button>
                          )}
                          <AdminRowMenu
                            label={`Actions for ${user.email}`}
                            items={[
                              {
                                label: 'View',
                                onSelect: () => setViewUser(user),
                              },
                              {
                                label: user.status === 'blocked' ? 'Unblock' : 'Block',
                                disabled: user.isEnvAdmin,
                                danger: user.status !== 'blocked',
                                onSelect: () => {
                                  if (user.status === 'blocked') unblockUser(user);
                                  else setBlockTarget(user);
                                },
                              },
                              {
                                label: 'Return to lobby',
                                disabled:
                                  user.isEnvAdmin || user.status === 'pending',
                                onSelect: () => returnToLobby(user),
                              },
                              user.platformRole === 'admin'
                                ? {
                                    label: 'Remove admin',
                                    disabled: user.isEnvAdmin,
                                    onSelect: () => removeAdmin(user),
                                  }
                                : {
                                    label: 'Make admin',
                                    disabled: user.isEnvAdmin,
                                    onSelect: () => makeAdmin(user),
                                  },
                              {
                                label: 'Delete',
                                disabled: user.isEnvAdmin,
                                danger: true,
                                onSelect: () => setDeleteTarget(user),
                              },
                            ]}
                          />
                        </div>
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            )}
          </AdminSectionCard>
        </div>
      </Commentable>

      <UserDetailDrawer
        user={viewUser}
        onClose={() => {
          setViewUser(null);
          setDrawerDeleteOpen(false);
        }}
        onSaveName={(name) => {
          if (viewUser) mutateUser(viewUser.id, { name: name.trim() || null });
        }}
        onBlock={() => {
          if (viewUser) setBlockTarget(viewUser);
        }}
        onUnblock={() => {
          if (viewUser) unblockUser(viewUser);
        }}
        onReturnToLobby={() => {
          if (viewUser) returnToLobby(viewUser);
        }}
        onToggleAdmin={() => {
          if (!viewUser) return;
          if (viewUser.platformRole === 'admin') removeAdmin(viewUser);
          else makeAdmin(viewUser);
        }}
        onDelete={() => {
          if (viewUser) {
            deleteUser(viewUser.id);
            setViewUser(null);
          }
        }}
        deleteOpen={drawerDeleteOpen}
        onDeleteOpenChange={setDrawerDeleteOpen}
      />

      <Modal
        open={blockTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setBlockTarget(null);
            setBlockReason('');
          }
        }}
        title="Block user"
      >
        {blockTarget ? (
          <div className="space-y-4">
            <p className="text-sm text-muted">
              Block <strong>{blockTarget.email}</strong> from signing in. Blocking signs the
              user out on their next request. Prefer blocking over deleting — it is reversible.
            </p>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
                Reason (optional)
              </span>
              <textarea
                value={blockReason}
                onChange={(event) => setBlockReason(event.target.value)}
                rows={3}
                className={FIELD_INPUT}
              />
            </label>
            <button
              type="button"
              className={cn(ADMIN_ACTION_BUTTON, 'text-red-600 dark:text-red-400')}
              onClick={() => {
                blockUser(blockTarget);
                setBlockTarget(null);
                setBlockReason('');
              }}
            >
              Block user
            </button>
          </div>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title="Delete user?"
        description={
          deleteTarget ? (
            <>
              Permanently delete <strong>{deleteTarget.email}</strong>? Their threads
              and project memberships will be removed in production — here this only
              updates local mock state.
            </>
          ) : null
        }
        confirmLabel="Delete"
        tone="danger"
        onConfirm={() => {
          if (deleteTarget) deleteUser(deleteTarget.id);
        }}
      />
    </AdminSceneShell>
  );
}
